import { Part } from "./Part";
import { Warnung3d } from "../arrangement3d/Warnung3d";
import { OBB } from "three/examples/jsm/math/OBB";
import { Jaco } from "oc-jaco3d/arrangement/Jaco";
import { IWarningDetailsDialog } from "oc-ui/dialogs/Dialogs";
import { Box3, Vector3 } from "three";

export type WarnungWichtigkeit = "niedrig" | "mittel" | "hoch";
export const WARNUNG_WICHTIGKEIT_DEFAULT: WarnungWichtigkeit = "hoch";
export type WarnungWichtigkeitColor = { hex: number; text: string };

const WARNUNG_WICHTIGKEIT_SETTING_KEYS: Record<WarnungWichtigkeit, string> = {
  niedrig: "warnungWichtigkeitNiedrigColor",
  mittel: "warnungWichtigkeitMittelColor",
  hoch: "warnungWichtigkeitHochColor",
};

function normalizeHexColor(value: unknown): string | null {
  if (value && typeof value === "object") {
    const colorLike = value as {
      getHex?: () => unknown;
      getHexString?: () => unknown;
    };

    if (typeof colorLike.getHexString === "function") {
      const hexString = colorLike.getHexString();
      if (typeof hexString === "string" && /^[0-9a-fA-F]{6}$/.test(hexString)) {
        return `#${hexString.toLowerCase()}`;
      }
    }

    if (typeof colorLike.getHex === "function") {
      const hex = colorLike.getHex();
      if (typeof hex === "number" && Number.isFinite(hex)) {
        const bounded = Math.max(0, Math.min(0xffffff, Math.trunc(hex)));
        return `#${bounded.toString(16).padStart(6, "0")}`;
      }
    }
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    const bounded = Math.max(0, Math.min(0xffffff, Math.trunc(value)));
    return `#${bounded.toString(16).padStart(6, "0")}`;
  }

  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const normalized = trimmed.replace(/^#/, "").replace(/^0x/i, "");

  if (/^[0-9]+$/.test(normalized)) {
    const asNumber = Number.parseInt(normalized, 10);
    if (Number.isFinite(asNumber)) {
      const bounded = Math.max(0, Math.min(0xffffff, asNumber));
      return `#${bounded.toString(16).padStart(6, "0")}`;
    }
  }

  if (/^[0-9a-fA-F]{3}$/.test(normalized)) {
    const shortHex = normalized.toLowerCase();
    return `#${shortHex
      .split("")
      .map((char) => char + char)
      .join("")}`;
  }

  if (/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return `#${normalized.toLowerCase()}`;
  }

  return null;
}

function toWarnungColor(textColor: string): WarnungWichtigkeitColor {
  return {
    text: textColor,
    hex: Number.parseInt(textColor.slice(1), 16),
  };
}

function getWarnungWichtigkeitColorFromSettings(wichtigkeit: WarnungWichtigkeit): string {
  const app = (globalThis as any)?.APP;
  const settingKey = WARNUNG_WICHTIGKEIT_SETTING_KEYS[wichtigkeit];
  const settingValue = app?.settings?.[settingKey];
  return normalizeHexColor(settingValue);
}

export function getWarnungWichtigkeitColor(
  wichtigkeit: WarnungWichtigkeit | null | undefined
): WarnungWichtigkeitColor {
  const key = wichtigkeit ?? WARNUNG_WICHTIGKEIT_DEFAULT;
  return toWarnungColor(getWarnungWichtigkeitColorFromSettings(key));
}

const warnungPriority: Record<WarnungWichtigkeit, number> = {
  niedrig: 1,
  mittel: 2,
  hoch: 3,
};

export type WarnungLike = {
  text?: string | null;
  wichtigkeit?: WarnungWichtigkeit | null;
  dialogConfig?: IWarningDetailsDialog | null;
};

export type MergedWarnung = {
  index: number;
  text: string;
  wichtigkeit: WarnungWichtigkeit;
  dialogConfig: IWarningDetailsDialog | null;
};

export function mergeWarnungenByText(
  warnungen: Array<WarnungLike | null | undefined>
): MergedWarnung[] {
  const warnungByText = new Map<
    string,
    { wichtigkeit: WarnungWichtigkeit; dialogConfig: IWarningDetailsDialog | null }
  >();
  for (const warnung of warnungen) {
    const text = warnung?.text?.trim();
    if (!text) {
      continue;
    }

    const wichtigkeit = warnung?.wichtigkeit ?? WARNUNG_WICHTIGKEIT_DEFAULT;
    const dialogConfig = warnung?.dialogConfig ?? null;
    const current = warnungByText.get(text);
    if (!current || warnungPriority[wichtigkeit] > warnungPriority[current.wichtigkeit]) {
      warnungByText.set(text, { wichtigkeit, dialogConfig });
      continue;
    }

    if (
      warnungPriority[wichtigkeit] === warnungPriority[current.wichtigkeit] &&
      !current.dialogConfig &&
      dialogConfig
    ) {
      current.dialogConfig = dialogConfig;
    }
  }

  return Array.from(warnungByText.entries()).map(
    ([text, { wichtigkeit, dialogConfig }], index) => ({
      index: index + 1,
      text,
      wichtigkeit,
      dialogConfig,
    })
  );
}

export interface WarnungZylinderSektor {
  type: "zylindersektor";
  pivot: Vector3;
  radiusInner: number;
  radiusOuter: number;
  yMin: number;
  yMax: number;
  startAngle: number;
  endAngle: number;
}

export class Warnung extends Part {
  static CLASS_NAME = "Warnung";

  isWarnung = true;
  text: string = null;
  box3OrOBB: any = new OBB();
  jacos: Jaco[] = [];
  positionYOffset: number = 0;
  dialogConfig: IWarningDetailsDialog | null = null;
  wichtigkeit: WarnungWichtigkeit = WARNUNG_WICHTIGKEIT_DEFAULT;

  constructor(parent) {
    super(parent);
  }

  clone() {
    return new Warnung(this.parent).copy(this);
  }

  createJaco3d() {
    return new Warnung3d(this);
  }

  toJSON() {
    const box = this.box3OrOBB?.toJSON?.() ?? null;
    const jacoIds = (this.jacos ?? [])
      .filter((jaco) => jaco != null && !(jaco as any).isWarnung && jaco.id != null)
      .map((jaco) => jaco.id);
    return {
      text: this.text,
      jacoIds,
      box,
    };
  }

  getBox() {
    const box3OrOBB = this.box3OrOBB;
    if (box3OrOBB?.type === "zylindersektor") {
      const r = box3OrOBB.radiusOuter;
      return new Box3(
        new Vector3(box3OrOBB.pivot.x - r, box3OrOBB.yMin, box3OrOBB.pivot.z - r),
        new Vector3(box3OrOBB.pivot.x + r, box3OrOBB.yMax, box3OrOBB.pivot.z + r)
      );
    }
    if (box3OrOBB.rotation) {
      return box3OrOBB.toBox3();
    }
    return box3OrOBB;
  }
}
